import { useRef, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCircle2, Download, Loader2, RefreshCw } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { requestGatedDownload } from "@/lib/downloads.functions";
import { DOWNLOAD_CONSENT_TEXT, GATED_ASSETS } from "@/lib/downloads.shared";
import { Checkbox } from "@/components/ui/checkbox";
import { trackCtaClick } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

type Props = {
  slug: keyof typeof GATED_ASSETS | string;
  label: string;
  className?: string;
  /** Omit for the English site; "tr" renders the Turkish copy. */
  locale?: "tr";
};

const COPY = {
  en: {
    privacyPath: "/privacy",
    consentRequired: "Please accept the privacy notice before we send the document.",
    genericError: "Something went wrong. Please try again or email info@oxory.net.",
    doneTitle: "Request confirmed",
    doneDescriptionPrefix: "Status of your request for",
    emailSent: "Email sent",
    emailNotSent: "Email not sent",
    emailSentBody: (email: string) =>
      `We sent the download link to ${email}. Please also check your spam folder.`,
    emailNotSentBody:
      "We could not deliver the email. You can retry below — the download link stays available here.",
    resend: "Resend email",
    retryEmail: "Retry email",
    resendFailed: "Sending failed again. Please email info@oxory.net.",
    downloadStarted: "Download started",
    downloadFailed: "Download did not start",
    downloadStartedBody:
      "The file was opened in your browser. If nothing happened, start it again below.",
    downloadFailedBody: "Your browser blocked the download. Start it again below.",
    startAgain: "Start download again",
    retryDownload: "Retry download",
    directLink: "Open direct link",
    close: "Close",
    formTitle: "Get the document by email",
    formDescription: (title: string) => (
      <>
        Enter your business email to receive <strong>{title}</strong>. The download starts right
        after.
      </>
    ),
    emailLabel: "Email address",
    emailPlaceholder: "you@company.com",
    companyLabel: "Company",
    consentBefore:
      "I agree that OXORY AS.DS GmbH may store my email address and send me the requested document. My data is processed only for this purpose and any related follow-up, is never passed on for advertising by third parties, and I can withdraw my consent at any time by emailing",
    consentMore: ". More in our",
    privacyLinkText: "privacy notice",
    gdprNote:
      "Your consent is stored together with your email address and a timestamp as proof under GDPR Art. 7.",
    submit: "Send me the download",
    required: "* required",
  },
  tr: {
    privacyPath: "/tr/gizlilik",
    consentRequired: "Belgeyi göndermemiz için lütfen gizlilik bildirimini kabul edin.",
    genericError:
      "Bir şeyler ters gitti. Lütfen tekrar deneyin veya info@oxory.net adresine yazın.",
    doneTitle: "Talebiniz onaylandı",
    doneDescriptionPrefix: "Şu belgeye ilişkin talebinizin durumu:",
    emailSent: "E-posta gönderildi",
    emailNotSent: "E-posta gönderilemedi",
    emailSentBody: (email: string) =>
      `İndirme bağlantısını ${email} adresine gönderdik. Lütfen spam klasörünüzü de kontrol edin.`,
    emailNotSentBody:
      "E-postayı iletemedik. Aşağıdan yeniden deneyebilirsiniz — indirme bağlantısı burada kullanılabilir durumda kalır.",
    resend: "E-postayı yeniden gönder",
    retryEmail: "E-postayı yeniden dene",
    resendFailed: "Gönderim yine başarısız oldu. Lütfen info@oxory.net adresine yazın.",
    downloadStarted: "İndirme başladı",
    downloadFailed: "İndirme başlamadı",
    downloadStartedBody:
      "Dosya tarayıcınızda açıldı. Hiçbir şey olmadıysa aşağıdan yeniden başlatın.",
    downloadFailedBody: "Tarayıcınız indirmeyi engelledi. Aşağıdan yeniden başlatın.",
    startAgain: "İndirmeyi yeniden başlat",
    retryDownload: "İndirmeyi yeniden dene",
    directLink: "Doğrudan bağlantıyı açın",
    close: "Kapat",
    formTitle: "Belgeyi e-posta ile alın",
    formDescription: (title: string) => (
      <>
        <strong>{title}</strong> belgesini almak için kurumsal e-posta adresinizi girin. İndirme
        hemen ardından başlar.
      </>
    ),
    emailLabel: "E-posta adresi",
    emailPlaceholder: "siz@sirketiniz.com",
    companyLabel: "Şirket",
    consentBefore:
      "OXORY AS.DS GmbH'nin e-posta adresimi saklamasını ve talep ettiğim belgeyi bana göndermesini kabul ediyorum. Verilerim yalnızca bu amaç ve buna bağlı iletişim için işlenir, üçüncü tarafların reklam amaçlı kullanımına asla verilmez ve onayımı dilediğim zaman şu adrese e-posta göndererek geri çekebilirim:",
    consentMore: ". Ayrıntılar:",
    privacyLinkText: "gizlilik bildirimimiz",
    gdprNote:
      "Onayınız, GDPR Madde 7 uyarınca kanıt olarak e-posta adresiniz ve zaman damgasıyla birlikte saklanır.",
    submit: "Bana indirme bağlantısını gönderin",
    required: "* zorunlu alan",
  },
} as const;

export function DownloadGate({ slug, label, className, locale }: Props) {
  const asset = GATED_ASSETS[slug];
  const submit = useServerFn(requestGatedDownload);
  const t = COPY[locale ?? "en"];

  const [open, setOpen] = useState(false);
  const openedAt = useRef(Date.now());
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [consent, setConsent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ file: string; emailed: boolean } | null>(null);
  const [downloadState, setDownloadState] = useState<"idle" | "started" | "error">("idle");
  const [resendState, setResendState] = useState<"idle" | "pending" | "sent" | "error">("idle");


  if (!asset) return null;

  function startDownload(file: string) {
    if (typeof window === "undefined") return;
    try {
      const link = document.createElement("a");
      link.href = file;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloadState("started");
    } catch {
      setDownloadState("error");
    }
  }



  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    if (!consent) {
      setError(t.consentRequired);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const result = await submit({
        data: {
          email,
          slug,
          sourcePath: typeof window !== "undefined" ? window.location.pathname : undefined,
          company,
          consent: true as const,
          consentText: DOWNLOAD_CONSENT_TEXT,
          startedAt: openedAt.current,
        },
      });
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setDone({ file: result.file, emailed: result.emailed });
      trackCtaClick(`Gated download: ${asset!.title}`, "download", result.file);
      startDownload(result.file);

    } catch {
      setError(t.genericError);
    } finally {
      setPending(false);
    }
  }

  async function onResend() {
    if (resendState === "pending") return;
    setResendState("pending");
    try {
      const result = await submit({
        data: {
          email,
          slug,
          sourcePath: typeof window !== "undefined" ? window.location.pathname : undefined,
          consent: true as const,
          consentText: DOWNLOAD_CONSENT_TEXT,
          startedAt: openedAt.current,
          resend: true,
        },
      });
      setResendState(result.ok && result.emailed ? "sent" : "error");
    } catch {
      setResendState("error");
    }
  }

  const emailOk = (done?.emailed ?? false) || resendState === "sent";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) openedAt.current = Date.now();
        if (!next) {
          setError(null);
          setDone(null);
          setResendState("idle");
          setDownloadState("idle");
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className={
            className ??
            "inline-flex items-center gap-2 text-sm font-semibold text-primary underline-offset-4 hover:underline"
          }
        >
          {label} <ArrowRight className="size-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {done ? (
          <>
            <DialogHeader>
              <DialogTitle>{t.doneTitle}</DialogTitle>
              <DialogDescription>
                {t.doneDescriptionPrefix} <strong>{asset.title}</strong>.
              </DialogDescription>
            </DialogHeader>

            <ul className="space-y-3 rounded-md border border-border bg-muted/40 p-3" aria-live="polite">
              <li className="flex items-start gap-3">
                {emailOk ? (
                  <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
                ) : (
                  <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden="true" />
                )}
                <div className="space-y-1">
                  <p className="text-sm font-semibold">
                    {emailOk ? t.emailSent : t.emailNotSent}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {emailOk ? t.emailSentBody(email) : t.emailNotSentBody}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={resendState === "pending"}
                      onClick={onResend}
                    >
                      {resendState === "pending" ? (
                        <Loader2 className="mr-2 size-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 size-4" />
                      )}
                      {emailOk ? t.resend : t.retryEmail}
                    </Button>
                    {resendState === "error" ? (
                      <span className="text-xs font-medium text-destructive">
                        {t.resendFailed}
                      </span>
                    ) : null}
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-3">
                {downloadState === "error" ? (
                  <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden="true" />
                ) : (
                  <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
                )}
                <div className="space-y-1">
                  <p className="text-sm font-semibold">
                    {downloadState === "error" ? t.downloadFailed : t.downloadStarted}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {downloadState === "error" ? t.downloadFailedBody : t.downloadStartedBody}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 pt-1">
                    <Button type="button" variant="outline" size="sm" onClick={() => startDownload(done.file)}>
                      <Download className="mr-2 size-4" />
                      {downloadState === "error" ? t.retryDownload : t.startAgain}
                    </Button>
                    <a
                      href={done.file}
                      download
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary underline-offset-4 hover:underline"
                    >
                      {t.directLink} <ArrowRight className="size-3" />
                    </a>
                  </div>
                </div>
              </li>
            </ul>

            <DialogFooter>
              <Button type="button" onClick={() => setOpen(false)} className="bg-primary text-primary-foreground hover:bg-primary/90">
                {t.close}
              </Button>
            </DialogFooter>

          </>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{t.formTitle}</DialogTitle>
              <DialogDescription>{t.formDescription(asset.title)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor={`download-email-${asset.slug}`}>{t.emailLabel} <span className="text-destructive">*</span></Label>
              <Input
                id={`download-email-${asset.slug}`}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t.emailPlaceholder}
              />
            </div>
            <p className="text-xs text-muted-foreground">{t.required}</p>
            <div className="hidden" aria-hidden="true">
              <label htmlFor={`download-company-${asset.slug}`}>{t.companyLabel}</label>
              <input
                id={`download-company-${asset.slug}`}
                tabIndex={-1}
                autoComplete="off"
                value={company}
                onChange={(event) => setCompany(event.target.value)}
              />
            </div>
            <div className="flex items-start gap-3 rounded-md border border-border bg-muted/40 p-3">
              <Checkbox
                id={`download-consent-${asset.slug}`}
                checked={consent}
                onCheckedChange={(value) => setConsent(value === true)}
                className="mt-0.5"
                aria-describedby={`download-consent-text-${asset.slug}`}
              />
              <Label
                htmlFor={`download-consent-${asset.slug}`}
                id={`download-consent-text-${asset.slug}`}
                className="text-xs font-normal leading-relaxed text-muted-foreground"
              >
                {t.consentBefore}{" "}
                <a href="mailto:info@oxory.net" className="text-primary underline-offset-4 hover:underline">
                  info@oxory.net
                </a>
                {t.consentMore}{" "}
                <a
                  href={t.privacyPath}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {t.privacyLinkText}
                </a>
                .
              </Label>
            </div>
            {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
            <p className="text-xs text-muted-foreground">{t.gdprNote}</p>
            <DialogFooter>
              <Button type="submit" disabled={pending || !consent} className="bg-primary text-primary-foreground hover:bg-primary/90">
                {pending ? <Loader2 className="size-4 animate-spin" /> : null}
                {t.submit}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
